import { Component, type ErrorInfo, type ReactNode } from 'react'
import { createLogger } from '@/lib/logger'
import type { AppError } from '@/lib/errors'

const log = createLogger('ErrorBoundary')

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    }

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Handle Vite ChunkLoadErrors (when a new deployment happens while the user is using the app)
        const isChunkLoadError =
            error?.message?.includes('Failed to fetch dynamically imported module') ||
            error?.message?.includes('Importing a module script failed');

        if (isChunkLoadError) {
            log.info('chunkLoadError', 'Detected chunk load error, forcing page reload to fetch new assets');
            window.location.reload();
            return;
        }

        const isAppError = 'code' in error && 'toLog' in error
        if (isAppError) {
            log.error('uncaughtAppError', 'Unhandled AppError reached boundary', {
                ...(error as AppError).toLog(),
                componentStack: errorInfo.componentStack ?? undefined,
            })
        } else {
            log.error('uncaughtError', 'Unhandled error reached boundary', {
                message: error.message,
                name: error.name,
                stack: error.stack,
                componentStack: errorInfo.componentStack ?? undefined,
            })
        }
    }

    public render() {
        if (this.state.hasError) {
            return this.props.fallback || (
                <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 text-foreground">
                    <div className="mx-auto flex max-w-md flex-col items-center text-center">
                        <h1 className="mb-4 text-3xl font-bold tracking-tight text-destructive">
                            Something went wrong
                        </h1>
                        <p className="mb-6 text-muted-foreground">
                            We apologize for the inconvenience. An unexpected error occurred.
                        </p>
                        {this.state.error && (
                            <pre className="mb-6 w-full overflow-auto rounded bg-muted/50 p-4 text-left text-xs text-muted-foreground">
                                {this.state.error.message}
                            </pre>
                        )}
                        <button
                            onClick={() => window.location.reload()}
                            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-8 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                        >
                            Reload Application
                        </button>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}
